import { publicationStateForSave } from '../../utils/formPublication';
import React, { useState, useEffect, useRef } from 'react';
import { usePostHog } from 'posthog-js/react';
import { useOnboardingDriver } from '../onboarding/GuidedFormCreation/OnboardingDriverContext';
import { getAuthToken, getUserEmail, fetchWithAuthRetry } from '../../utils/auth';
import { createDoubleTheDonationClient } from '../../lib/trpc/double-the-donation-main';
import ReactTooltip from 'react-tooltip';
import FormSharingCards from './FormSharingCards';
import CustomInputsTab from './tabs/CustomInputsTab';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { toast } from 'sonner';
import { copyToClipboard } from '@charitystack/shared';
import OverlaySnippets from './OverlaySnippets';
import ScrollFade from '../../components/ui/scroll-fade';
import { uploadImage, deleteImage } from '@/api/images';
import {
  persistFormImageVersion,
  VERSION_NOT_SAVED_MESSAGE,
} from '@/api/images/persistImageVersion';
import { safeFetchJSON } from '@charitystack/shared';
import { getValidationToastMessage } from '../../utils/validationToast';
import { validateFundraiserFormForSave } from '../../utils/fundraiserFormValidation';
import { DASHBOARD_PLG_EVENTS, trackDashboardPlg } from '../../utils/dashboardPlgAnalytics';
import { useLatestCallback } from '../../hooks/useLatestCallback';

import CustomFAQsTab from './tabs/CustomFAQsTab';
import EmailConfigurationTab from './tabs/EmailConfigurationTab';
import { FormWorkspaceTabSection } from '../../components/workspaces/forms/FormWorkspaceTabSection';
import FormEditorSectionJump from '../../components/workspaces/forms/FormEditorSectionJump';
import {
  getFormEditorConfigurationColumnClassName,
  getFormEditorLayoutClassName,
  getFormEditorPreviewColumnClassName,
  getFormEditorPreviewFrameClassName,
} from '../../components/workspaces/forms/formEditorWorkspaceLayout';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { COLORS } from '../../constants/colors';
// @ts-expect-error - QRCodeModal is a JSX file without type definitions
import QRCodeModal from '../modals/QRCodeModal';
import FundraiserFormConfigurationTab, {
  type CrowdfundingOption,
  type ProductGroup,
} from './tabs/FundraiserFormConfigurationTab';
import FundraiserFormPreview from '../previewer/FundraiserFormPreview';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
// @ts-expect-error - FieldMapperModal is a JSX file without type definitions
import FieldMapperModal from '../modals/FieldMapperModal';
import SalesforceFieldMapperModal from '../modals/SalesforceFieldMapperModal';

import {
  CreateFundraiserDrawerProps,
  ActiveTab,
  StepType,
  CustomFAQ,
  EmailConfiguration,
  FrequencyAmountsMap,
  FrequencyScheduledRangeMap,
  FormPayload,
  CustomInput,
  Tab,
  type WorkspaceHeaderAction,
} from './CreateFundraiserDrawer.types';

const tabs: Tab[] = [
  { id: 'form_configuration', name: 'Form Configuration', current: true },
  { id: 'custom_fields', name: 'Custom Input Fields', current: false },
  { id: 'faqs', name: 'FAQs', current: false },
  { id: 'emails', name: 'Emails', current: false },
];

// Initial form data structure - consolidated state
const initialFormData = {
  // Basic form info
  formName: '',
  formID: '',
  elementID: '',
  active: true,

  // Form type (EMBED_FORM, CROWDFUNDING_FORM, PRODUCT)
  formType: 'EMBED_FORM',

  // Fundraiser type
  fundraiserType: 'DONATION_FORM',

  // Appearance
  color: COLORS.FORM_DEFAULT_COLOR,
  image: null as string | null,
  resizedImage: null as string | null,
  imageWasUploaded: false, // Track if an image was ever uploaded (for delete logic)

  // Target and offsets
  targetAmount: '3000',
  amountOffset: '',
  countOffset: '',
  totalTransactionAmount: 0,
  showFundraisingBar: true,
  trackingMetric: 'DOLLAR_AMOUNT',
  metricLabel: 'Sustainers',
  subscriptionCount: 0,
  enableDonorList: true,
  donorListType: 'RECENT_DONORS',
  showAllSupportersAnonymous: false,
  showProductGroups: false,
  enableEmbedForm: true,
  fundraisingBarElementID: '',
  donorListElementID: '',

  // Donor list data from API response
  donorListData: null as { recentDonors?: any[]; topDonors?: any[] } | null,

  // Frequencies and amounts
  defaultDonationType: 'One-Time',
  checkedValues: ['One-Time', 'Monthly'] as string[],
  frequencyAmounts: {
    'One-Time': {
      amount1: '25',
      amount2: '50',
      amount3: '100',
      amount4: '250',
      defaultAmount: '50',
    },
    Daily: {
      amount1: '',
      amount2: '',
      amount3: '',
      amount4: '',
      defaultAmount: '',
    },
    Weekly: {
      amount1: '',
      amount2: '',
      amount3: '',
      amount4: '',
      defaultAmount: '',
    },
    Monthly: {
      amount1: '25',
      amount2: '50',
      amount3: '100',
      amount4: '250',
      defaultAmount: '50',
    },
    Annually: {
      amount1: '',
      amount2: '',
      amount3: '',
      amount4: '',
      defaultAmount: '',
    },
  } as FrequencyAmountsMap,

  // Scheduled donations
  requireScheduleDonations: false,
  frequencyScheduledRanges: {
    Daily: {
      enabled: false,
      scheduleType: 'immediate',
      startDateOffset: null,
      endDateOffset: null,
      scheduledTime: '',
    },
    Weekly: {
      enabled: false,
      scheduleType: 'immediate',
      startDateOffset: null,
      endDateOffset: null,
      scheduledTime: '',
    },
    Monthly: {
      enabled: false,
      scheduleType: 'immediate',
      startDateOffset: null,
      endDateOffset: null,
      scheduledTime: '',
    },
    Annually: {
      enabled: false,
      scheduleType: 'immediate',
      startDateOffset: null,
      endDateOffset: null,
      scheduledTime: '',
    },
  } as FrequencyScheduledRangeMap,
  activeScheduleTab: '' as string, // Will be set to first recurring frequency when enabled

  // Checkout options
  showAnonymousDonations: false,
  showOrganizationNameField: false,
  enablePhoneNumber: false,
  enableBillingAddress: false,
  enableDoubleTheDonation: false,
  selectedCountry: 'Choose Default Country',
  enableCommunicationConsent: true,
  communicationConsentMessage: '',
  enableDedication: false,
  dedicationTypes: ['In Honor Of', 'In Memory Of'] as string[],
  enableDedicationMessage: false,
  lockCoverFees: false,
  disableCheckoutBump: false,

  // Double the donation API key
  dtdPublicApiKey: null as string | null,

  // Funds
  funds: [] as string[],

  // Collections
  customInputs: [] as CustomInput[],
  faqs: [] as CustomFAQ[],

  // Crowdfunding and Product Groups (Legacy)
  crowdfundingOptions: [] as CrowdfundingOption[],
  productGroups: [] as ProductGroup[],

  // Per-frequency Crowdfunding Options (NEW - mirrors Standard form pattern)
  oneTimeCrowdfundingOptions: [] as CrowdfundingOption[],
  dailyCrowdfundingOptions: [] as CrowdfundingOption[],
  weeklyCrowdfundingOptions: [] as CrowdfundingOption[],
  monthlyCrowdfundingOptions: [] as CrowdfundingOption[],
  annuallyCrowdfundingOptions: [] as CrowdfundingOption[],

  // Per-frequency Product Groups (NEW - mirrors Standard form pattern)
  oneTimeProductGroups: [] as ProductGroup[],
  dailyProductGroups: [] as ProductGroup[],
  weeklyProductGroups: [] as ProductGroup[],
  monthlyProductGroups: [] as ProductGroup[],
  annuallyProductGroups: [] as ProductGroup[],

  // Default selections per frequency (optional)
  defaultOneTimeCrowdfundingOption: undefined as CrowdfundingOption | undefined,
  defaultDailyCrowdfundingOption: undefined as CrowdfundingOption | undefined,
  defaultWeeklyCrowdfundingOption: undefined as CrowdfundingOption | undefined,
  defaultMonthlyCrowdfundingOption: undefined as CrowdfundingOption | undefined,
  defaultAnnuallyCrowdfundingOption: undefined as CrowdfundingOption | undefined,

  // Rich text content
  details: '' as string | null,
  description: '',

  // Field mappings
  fieldMappings: [] as any[],

  // Email configuration
  emailConfig: {
    enableCustomEmail: false,
    replyToAddress: '',
    subject: '',
    customMessage: '',
  } as EmailConfiguration,
};

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

/** Normalize org-setup brand color for form color field */
function normalizeOnboardingBrandHex(input: string): string {
  const s = (input || '').trim();
  if (!s) return COLORS.FORM_DEFAULT_COLOR;
  const withHash = s.startsWith('#') ? s : `#${s}`;
  return /^#[0-9A-Fa-f]{6}$/i.test(withHash) ? withHash : COLORS.FORM_DEFAULT_COLOR;
}

/** Same preset as FundraiserFormConfigurationTab `handleFormTypeChange` for EMBED_FORM standard amounts */
const STANDARD_EMBED_AMOUNT_ROW = {
  amount1: '25',
  amount2: '50',
  amount3: '100',
  amount4: '250',
  defaultAmount: '50',
};

const EMPTY_EMBED_AMOUNT_ROW = {
  amount1: '',
  amount2: '',
  amount3: '',
  amount4: '',
  defaultAmount: '',
};

function buildStandardFrequencyAmountsForChecked(checked: string[]): FrequencyAmountsMap {
  const row = (freq: string) =>
    checked.includes(freq) ? { ...STANDARD_EMBED_AMOUNT_ROW } : { ...EMPTY_EMBED_AMOUNT_ROW };
  return {
    'One-Time': row('One-Time'),
    Daily: row('Daily'),
    Weekly: row('Weekly'),
    Monthly: row('Monthly'),
    Annually: row('Annually'),
  };
}

const HOSTED_PAGE_URL = import.meta.env.VITE_APP_MONOREPO_BASE_URL;

const newShade = (hexColor: string, magnitude: number): string => {
  const cleanHex = hexColor.replace(`#`, ``);
  if (cleanHex.length === 6) {
    const decimalColor = parseInt(cleanHex, 16);
    let r = (decimalColor >> 16) + magnitude;
    if (r > 255) r = 255;
    if (r < 0) r = 0;
    let g = (decimalColor & 0x0000ff) + magnitude;
    if (g > 255) g = 255;
    if (g < 0) g = 0;
    let b = ((decimalColor >> 8) & 0x00ff) + magnitude;
    if (b > 255) b = 255;
    if (b < 0) b = 0;
    return `#${(g | (b << 8) | (r << 16)).toString(16)}`.toUpperCase();
  } else {
    return cleanHex.toUpperCase();
  }
};

export default function CreateFundraiserDrawer(props: CreateFundraiserDrawerProps) {
  const { selectableFunds, grabSelectableFunds } = props;
  const onboardingCtx = useOnboardingDriver();
  const paymentsReady = onboardingCtx?.chargesEnabled === true;
  const posthog = usePostHog();

  // Consolidated form data state
  const [formData, setFormData] = useState(initialFormData);

  // Helper functions for updating form data
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateFormData = (field: keyof typeof initialFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // UI and system states (keep separate as they're not form data)
  const [merchantName, setMerchantName] = useState<string>('');

  const [activeTab, setActiveTab] = useState<ActiveTab>({
    id: 'form_configuration',
    name: 'Form Configuration',
    current: true,
  });

  // Step management for form configuration
  const [currentStep, setCurrentStep] = useState<StepType>('appearance');

  // Preview tab management
  const [activePreviewTab, setActivePreviewTab] = useState('Embed Form');
  const [previewKey, setPreviewKey] = useState(0);
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const configurationSectionRef = React.useRef<HTMLDivElement>(null);
  const previewSectionRef = React.useRef<HTMLDivElement>(null);

  const [createdForm, setCreatedForm] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isActivating, setIsActivating] = useState(false);

  // Bloomerange values
  const [showFieldMappingModal, setShowFieldMappingModal] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [bloomerangAppeals, setBloomerangAppeals] = useState<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [bloomerangCampaigns, setBloomerangCampaigns] = useState<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [bloomerangFunds, setBloomerangFunds] = useState<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [virtuousProjects, setVirtuousProjects] = useState<any[]>([]);

  // Validation state
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());

  // Track if we've initialized default items to prevent re-initialization
  const [hasInitializedDefaults, setHasInitializedDefaults] = useState(false);

  // Rich text editor states (complex object, keep separate)
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
  const [convertedContent, setConvertedContent] = useState<string | null>(null);

  // QR Code modal states
  const [showQRModal, setShowQRModal] = useState(false);
  const [savedQRCodeData] = useState<{ url: string } | null>(null);

  const onboardingPrefillAppliedRef = useRef(false);

  // Source website and Salesforce Campaign ID states (for ICNA Relief and other nonprofits)

  const [sourceWebsite, setSourceWebsite] = useState<string[]>([]);
  const [sourceWebsiteValues, setSourceWebsiteValues] = useState<string>('');

  const [salesforceCampaignIDICNA, setSalesforceCampaignIDICNA] = useState<string[]>([]);
  const [salesforceCampaignIDICNAValues, setSalesforceCampaignIDICNAValues] = useState<string>('');
  const [salesforceFieldMapperOpen, setSalesforceFieldMapperOpen] = useState(false);

  let integrationType = null;
  if (props.merchant?.netSuiteConnectionEnabled === true) {
    integrationType = 'NETSUITE';
  } else if (props.merchant?.bloomerangConnectionEnabled === true) {
    integrationType = 'BLOOMERANG';
  } else if (props.merchant?.virtuousConnectionEnabled === true) {
    integrationType = 'VIRTUOUS';
  }

  // Step navigation functions
  const handleContinue = () => {
    const nextStepMap: Record<StepType, StepType | 'save'> = {
      appearance: 'frequency',
      frequency: 'amounts',
      amounts: 'checkout',
      checkout: 'save',
    };

    if (currentStep === 'checkout') {
      // This is the final step, save the configuration
      handleSaveConfiguration();
    } else {
      const nextStep = nextStepMap[currentStep];
      if (nextStep !== 'save') {
        setCurrentStep(nextStep);
      }
    }
  };

  const handleBack = () => {
    const prevStepMap: Record<StepType, StepType | undefined> = {
      appearance: undefined,
      frequency: 'appearance',
      amounts: 'frequency',
      checkout: 'amounts',
    };

    const prevStep = prevStepMap[currentStep];
    if (prevStep) {
      setCurrentStep(prevStep);
    }
  };

  const handleSaveConfiguration = () => {
    // Call sendToServer to save the fundraiser
    sendToServer();
  };

  // Validation functions
  const handleFieldFocus = (fieldName: string) => {
    setInvalidFields((prev) => {
      const newInvalidFields = new Set(prev);
      // Remove the exact field name
      newInvalidFields.delete(fieldName);
      // Also try to remove non-prefixed version for backward compatibility
      const nonPrefixedName = fieldName.includes('-')
        ? fieldName.split('-').slice(1).join('-')
        : fieldName;
      newInvalidFields.delete(nonPrefixedName);
      return newInvalidFields;
    });
  };

  const isFieldInvalid = (fieldName: string) => {
    if (!invalidFields) return false;
    if (invalidFields instanceof Set) {
      // Check for exact match first
      if (invalidFields.has(fieldName)) {
        return true;
      }
      // Check for prefixed versions
      const formTabs = ['form_configuration', 'custom_fields', 'faqs', 'emails'];
      for (const tab of formTabs) {
        if (invalidFields.has(`${tab}-${fieldName}`)) {
          return true;
        }
      }
      return false;
    }
    return false;
  };

  // Helper function to check if a tab has validation errors
  const getTabValidationErrors = (tabName: string) => {
    if (!invalidFields || !(invalidFields instanceof Set)) return false;

    const tabPrefix = getTabPrefix(tabName);
    if (!tabPrefix) return false;

    return Array.from(invalidFields).some((field) => field.startsWith(tabPrefix));
  };

  // Helper function to get tab prefix for validation
  const getTabPrefix = (tabName: string): string | null => {
    switch (tabName) {
      case 'Form Configuration':
        return 'form_configuration-';
      case 'Custom Input Fields':
        return 'custom_fields-';
      case 'FAQs':
        return 'faqs-';
      case 'Emails':
        return 'emails-';
      default:
        return null;
    }
  };

  // Clipboard copy functions
  const handleCopyUrl = async () => {
    const url = `${HOSTED_PAGE_URL}/donate/${formData.elementID}`;
    const success = await copyToClipboard(url);
    if (success) {
      toast.success('URL copied to clipboard');
    } else {
      toast.error('Failed to copy URL');
    }
  };

  const handleCopyEmbedCode = async () => {
    const embedCode = `<div data-entity="EMBED_FORM" data-source="CharityStack" id="${formData.elementID}"></div>`;
    const success = await copyToClipboard(embedCode);
    if (success) {
      toast.success('Embed code copied to clipboard');
    } else {
      toast.error('Failed to copy embed code');
    }
  };

  // Helper function to sort checked frequencies in the correct order
  const sortCheckedFrequencies = (frequencies: string[]): string[] => {
    const frequencyOptions = ['One-Time', 'Daily', 'Weekly', 'Monthly', 'Annually'];
    return frequencyOptions.filter((freq) => frequencies.includes(freq));
  };

  // Wrapper function to handle checkedValues updates with proper sorting
  const handleCheckedValuesUpdate = (newValues: string[]) => {
    const sortedValues = sortCheckedFrequencies(newValues);
    updateFormData('checkedValues', sortedValues);
  };

  // New forms inherit the current organization's branding without reading another
  // user's signup drafts from shared browser storage.
  useEffect(() => {
    if (!props.open) { onboardingPrefillAppliedRef.current = false; return; }
    if (onboardingPrefillAppliedRef.current || !props.merchant || formData.formID) return;
    onboardingPrefillAppliedRef.current = true;
    setFormData((prev) => prev.formName ? prev : {
      ...prev,
      formName: `Support ${props.merchant?.nonprofitName || props.merchant?.merchantName || 'our mission'}`,
      color: normalizeOnboardingBrandHex(String(props.merchant?.brandColor || props.merchant?.lightColor || '')) as typeof prev.color,
    });
  }, [props.open, props.merchant]);

  // Helper function to convert frequency names to backend format (all caps)
  const convertFrequencyToBackendFormat = (freq: string): string => {
    const mapping: Record<string, string> = {
      'One-Time': 'ONE_TIME',
      Daily: 'DAILY',
      Weekly: 'WEEKLY',
      Monthly: 'MONTHLY',
      Annually: 'ANNUALLY',
    };
    return mapping[freq] || freq;
  };

  const sendToServer = async () => {
    const saveValidation = validateFundraiserFormForSave(formData, {
      descriptionPlainText: editorState.getCurrentContent().getPlainText(''),
    });
    if (saveValidation.invalidFields.size > 0) {
      setInvalidFields(saveValidation.invalidFields);
      toast.error(getValidationToastMessage(saveValidation.errorMessages));
      return;
    }
    const saveFormData = saveValidation.formData;
    if (saveFormData !== formData) {
      updateFormData('oneTimeProductGroups', saveFormData.oneTimeProductGroups || []);
      updateFormData('dailyProductGroups', saveFormData.dailyProductGroups || []);
      updateFormData('weeklyProductGroups', saveFormData.weeklyProductGroups || []);
      updateFormData('monthlyProductGroups', saveFormData.monthlyProductGroups || []);
      updateFormData('annuallyProductGroups', saveFormData.annuallyProductGroups || []);
    }

    if (saveValidation.warningMessages.length > 0) {
      toast.warning(getValidationToastMessage(saveValidation.warningMessages));
    }

    setIsPublishing(true);
    console.log('[PUBLISH 0] ====== PUBLISH STARTED ======', {
      createdForm,
    });

    // Build frequencies array in the format backend expects (all caps)
    const frequencies = formData.checkedValues.map(convertFrequencyToBackendFormat);

    // Build one-time amounts array
    const ot = formData.frequencyAmounts['One-Time'];
    const oneTimeAmounts = ot
      ? [ot.amount1, ot.amount2, ot.amount3, ot.amount4]
          .filter((amount) => amount && amount.trim())
          .map((amount) => parseFloat(amount))
      : [];

    // Build frequency-specific amounts (only for checked frequencies)
    const monthlyAmounts =
      formData.checkedValues.includes('Monthly') && formData.frequencyAmounts['Monthly']
        ? [
            formData.frequencyAmounts['Monthly'].amount1,
            formData.frequencyAmounts['Monthly'].amount2,
            formData.frequencyAmounts['Monthly'].amount3,
            formData.frequencyAmounts['Monthly'].amount4,
          ]
            .filter((amount) => amount && amount.trim())
            .map((amount) => parseFloat(amount))
        : [];

    const weeklyAmounts =
      formData.checkedValues.includes('Weekly') && formData.frequencyAmounts['Weekly']
        ? [
            formData.frequencyAmounts['Weekly'].amount1,
            formData.frequencyAmounts['Weekly'].amount2,
            formData.frequencyAmounts['Weekly'].amount3,
            formData.frequencyAmounts['Weekly'].amount4,
          ]
            .filter((amount) => amount && amount.trim())
            .map((amount) => parseFloat(amount))
        : [];

    const dailyAmounts =
      formData.checkedValues.includes('Daily') && formData.frequencyAmounts['Daily']
        ? [
            formData.frequencyAmounts['Daily'].amount1,
            formData.frequencyAmounts['Daily'].amount2,
            formData.frequencyAmounts['Daily'].amount3,
            formData.frequencyAmounts['Daily'].amount4,
          ]
            .filter((amount) => amount && amount.trim())
            .map((amount) => parseFloat(amount))
        : [];

    const annuallyAmounts =
      formData.checkedValues.includes('Annually') && formData.frequencyAmounts['Annually']
        ? [
            formData.frequencyAmounts['Annually'].amount1,
            formData.frequencyAmounts['Annually'].amount2,
            formData.frequencyAmounts['Annually'].amount3,
            formData.frequencyAmounts['Annually'].amount4,
          ]
            .filter((amount) => amount && amount.trim())
            .map((amount) => parseFloat(amount))
        : [];

    // Build default amounts
    const defaultOneTimeAmount =
      formData.checkedValues.includes('One-Time') && ot?.defaultAmount
        ? parseFloat(ot.defaultAmount)
        : 0;
    const defaultMonthlyAmount =
      formData.checkedValues.includes('Monthly') &&
      formData.frequencyAmounts['Monthly']?.defaultAmount
        ? parseFloat(formData.frequencyAmounts['Monthly'].defaultAmount)
        : 0;
    const defaultWeeklyAmount =
      formData.checkedValues.includes('Weekly') &&
      formData.frequencyAmounts['Weekly']?.defaultAmount
        ? parseFloat(formData.frequencyAmounts['Weekly'].defaultAmount)
        : 0;
    const defaultDailyAmount =
      formData.checkedValues.includes('Daily') && formData.frequencyAmounts['Daily']?.defaultAmount
        ? parseFloat(formData.frequencyAmounts['Daily'].defaultAmount)
        : 0;
    const defaultAnnuallyAmount =
      formData.checkedValues.includes('Annually') &&
      formData.frequencyAmounts['Annually']?.defaultAmount
        ? parseFloat(formData.frequencyAmounts['Annually'].defaultAmount)
        : 0;

    // Process funds array (ensure it's in the format backend expects)
    const fundsArray = formData.funds || [];

    // Determine tracking metric once for offset scoping in payload
    // All count metrics use count-based offsets (countOffset / elementOffset)
    const isCountMetric = ['SUBSCRIPTION_COUNT', 'PRODUCT_COUNT', 'DONATION_COUNT'].includes(
      formData.trackingMetric || 'DOLLAR_AMOUNT',
    );

    // Build complete payload matching backend schema
    const payload: FormPayload = {
      formID: formData.formID || undefined,
      formName: formData.formName,
      formType: formData.formType,
      fundraiserType: formData.fundraiserType,
      merchantName: merchantName,

      // Amounts and frequencies
      frequencies: frequencies,
      defaultFrequency: convertFrequencyToBackendFormat(formData.defaultDonationType),
      oneTimeAmounts: oneTimeAmounts,
      monthlyAmounts: monthlyAmounts,
      weeklyAmounts: weeklyAmounts,
      dailyAmounts: dailyAmounts,
      annuallyAmounts: annuallyAmounts,
      defaultOneTimeAmount: defaultOneTimeAmount,
      defaultMonthlyAmount: defaultMonthlyAmount,
      defaultWeeklyAmount: defaultWeeklyAmount,
      defaultDailyAmount: defaultDailyAmount,
      defaultAnnuallyAmount: defaultAnnuallyAmount,
      enableEmbedForm: formData.enableEmbedForm,
      fieldMappings: formData.fieldMappings,
      showAddToCart: false, // Default for fundraisers

      // Fundraiser specific fields
      targetAmount: parseInt(formData.targetAmount) || 0,
      funds: fundsArray,
      lightColor: formData.color || '#9A9285',
      darkColor: formData.color ? newShade(formData.color, -50) : '#6B6458',

      // Campaign settings
      requireScheduleDonations: formData.requireScheduleDonations,
      dtdPublicApiKey: formData.dtdPublicApiKey || null,

      // Source website and Salesforce Campaign ID (required by backend for PRODUCT, DONATION_FORM, and DONATION_FLAG)
      // These are conditionally added below if they have values
      sourceWebsite: '', // Default empty - backend uses this for element creation
      salesforceCampaignID: '', // Default empty - backend uses this for element creation

      // Default scheduled range for donations (per-frequency)
      ...(formData.requireScheduleDonations && formData.frequencyScheduledRanges
        ? {
            defaultScheduledRange: formData.frequencyScheduledRanges,
          }
        : {}),

      // Form elements
      customInputs: formData.customInputs.filter((input) => input.title && input.type),
      faqs: formData.faqs.filter((faq) => faq.question && faq.answer),
      details: convertedContent || '',
      eventDetails: null, // Null for fundraisers
      // Display options
      showFundraisingBar: formData.showFundraisingBar,
      amountOffset: parseInt(formData.amountOffset) || 0,
      countOffset: parseInt(formData.countOffset) || 0,
      trackingMetric: formData.trackingMetric || 'DOLLAR_AMOUNT',
      metricLabel: formData.metricLabel || 'Sustainers',

      // Checkout configuration
      showAnonymousDonations: formData.showAnonymousDonations,
      showOrganizationNameField: formData.showOrganizationNameField,
      requirePhoneNumber: formData.enablePhoneNumber,
      requireBillingAddress: formData.enableBillingAddress,
      requireDoubleTheDonation: formData.enableDoubleTheDonation,
      enableCommunicationConsent: formData.enableCommunicationConsent,
      communicationConsentMessage: formData.communicationConsentMessage,
      enableDedication: formData.enableDedication,
      dedicationTypes: formData.dedicationTypes,
      enableDedicationMessage: formData.enableDedicationMessage,
      lockCoverFees: formData.lockCoverFees,
      disableCheckoutBump: formData.disableCheckoutBump,
      selectedCountry:
        formData.selectedCountry !== 'Choose Default Country'
          ? formData.selectedCountry
          : undefined,

      // Email and donor settings
      enableDonorList: formData.enableDonorList,
      donorListType: formData.enableDonorList ? formData.donorListType : '',

      // Form status
      active: publicationStateForSave({ existingId: formData.formID, active: formData.active, paymentsReady }),

      // Element ID (for updates only)
      ...(formData.formID ? { elementID: formData.formID } : {}),
      // NOTE: Images are uploaded separately via handleImageSubmit() after form creation.
      // For URL-based images (enrichment logos), the prefill converts them to base64
      // so they go through the same upload flow and land at the expected S3 path.

      // Product groups - always preserve data so users can switch form types without losing config
      showProductGroups: formData.showProductGroups,
      productGroups: (() => {
        const dfMap: Record<string, unknown[] | undefined> = {
          'One-Time': saveFormData.oneTimeProductGroups,
          Daily: saveFormData.dailyProductGroups,
          Weekly: saveFormData.weeklyProductGroups,
          Monthly: saveFormData.monthlyProductGroups,
          Annually: saveFormData.annuallyProductGroups,
        };
        return dfMap[formData.defaultDonationType] || saveFormData.productGroups || [];
      })(),

      // Crowdfunding options - always preserve data so users can switch form types without losing config
      crowdfundingOptions: (() => {
        const dfMap: Record<string, unknown[] | undefined> = {
          'One-Time': formData.oneTimeCrowdfundingOptions,
          Daily: formData.dailyCrowdfundingOptions,
          Weekly: formData.weeklyCrowdfundingOptions,
          Monthly: formData.monthlyCrowdfundingOptions,
          Annually: formData.annuallyCrowdfundingOptions,
        };
        return dfMap[formData.defaultDonationType] || formData.crowdfundingOptions || [];
      })(),

      // Per-frequency arrays for Giving Levels
      oneTimeCrowdfundingOptions: formData.oneTimeCrowdfundingOptions || [],
      dailyCrowdfundingOptions: formData.dailyCrowdfundingOptions || [],
      weeklyCrowdfundingOptions: formData.weeklyCrowdfundingOptions || [],
      monthlyCrowdfundingOptions: formData.monthlyCrowdfundingOptions || [],
      annuallyCrowdfundingOptions: formData.annuallyCrowdfundingOptions || [],

      // Per-frequency arrays for Sponsorships
      oneTimeProductGroups: saveFormData.oneTimeProductGroups || [],
      dailyProductGroups: saveFormData.dailyProductGroups || [],
      weeklyProductGroups: saveFormData.weeklyProductGroups || [],
      monthlyProductGroups: saveFormData.monthlyProductGroups || [],
      annuallyProductGroups: saveFormData.annuallyProductGroups || [],

      // Default selections per frequency
      defaultOneTimeCrowdfundingOption: formData.defaultOneTimeCrowdfundingOption,
      defaultDailyCrowdfundingOption: formData.defaultDailyCrowdfundingOption,
      defaultWeeklyCrowdfundingOption: formData.defaultWeeklyCrowdfundingOption,
      defaultMonthlyCrowdfundingOption: formData.defaultMonthlyCrowdfundingOption,
      defaultAnnuallyCrowdfundingOption: formData.defaultAnnuallyCrowdfundingOption,

      // Only include elements on CREATE (POST), not on UPDATE (PATCH)
      // PATCH requests should not include elements array to avoid backend errors
      // NOTE: Backend automatically creates the main form element (DONATION_FORM/etc)
      // We only send auxiliary elements like FUNDRAISING_BAR, DONOR_LIST, and QR_CODE
      elements: !createdForm
        ? [
            // Fundraising bar element (if enabled)
            ...(formData.showFundraisingBar
              ? [
                  {
                    type: 'FUNDRAISING_BAR',
                    goal: parseInt(formData.targetAmount) || 100000,
                    embedSize: 'SMALL', // Default size
                    donationsOffset: isCountMetric ? parseInt(formData.countOffset) || 0 : 0,
                    raisedOffset: isCountMetric ? 0 : parseInt(formData.amountOffset) || 0,
                    trackingMetric: formData.trackingMetric || 'DOLLAR_AMOUNT',
                    metricLabel: formData.metricLabel || 'Sustainers',
                    elementOffset: isCountMetric ? parseInt(formData.countOffset) || 0 : 0,
                  },
                ]
              : []),
            // Donor list element (if enabled)
            ...(formData.enableDonorList
              ? [
                  {
                    type: 'DONOR_LIST',
                    listType: formData.donorListType?.toUpperCase() || 'RECENT_DONORS',
                    showAllSupportersAnonymous: formData.showAllSupportersAnonymous ?? false,
                  },
                ]
              : []),
            // QR Code element (always included on CREATE)
            {
              type: 'QR_CODE',
              qrCodeType: 'HOSTED_PAGE',
              url: '', // Will be populated by backend after form creation
            },
          ]
        : [
            // Donor list element (if enabled) - UPDATE format
            ...(formData.enableDonorList
              ? [
                  {
                    type: 'DONOR_LIST',
                    id: formData.elementID || '',
                    isSaved: true,
                    savedIndicies: [0, 1],
                    selectedValuesSoFar: ['Donor List'],
                    listType: formData.donorListType?.toUpperCase() || 'RECENT_DONORS',
                    showAllSupportersAnonymous: formData.showAllSupportersAnonymous ?? false,
                    embedSize: '',
                    goal: '',
                    qrCodeType: '',
                    url: '',
                    donationsOffset: '',
                    raisedOffset: '',
                  },
                ]
              : []),
            // Fundraising bar element (if enabled) - UPDATE format
            ...(formData.showFundraisingBar
              ? [
                  {
                    type: 'FUNDRAISING_BAR',
                    id: formData.elementID || '',
                    isSaved: true,
                    savedIndicies: formData.enableDonorList ? [0, 1] : [0],
                    selectedValuesSoFar: formData.enableDonorList
                      ? ['Donor List', 'Fundraising Bar']
                      : ['Fundraising Bar'],
                    goal: formData.targetAmount || '10000',
                    embedSize: 'REGULAR',
                    donationsOffset: isCountMetric ? parseInt(formData.countOffset) || 0 : 0,
                    raisedOffset: isCountMetric ? 0 : parseInt(formData.amountOffset) || 0,
                    trackingMetric: formData.trackingMetric || 'DOLLAR_AMOUNT',
                    metricLabel: formData.metricLabel || 'Sustainers',
                    elementOffset: isCountMetric ? parseInt(formData.countOffset) || 0 : 0,
                    listType: '',
                    qrCodeType: '',
                    url: '',
                  },
                ]
              : []),
            // QR Code element (always included on UPDATE)
            {
              type: 'QR_CODE',
              id: formData.elementID || '',
              isSaved: true,
              // savedIndicies should only contain indices of elements BEFORE this one, not including itself
              savedIndicies:
                formData.enableDonorList && formData.showFundraisingBar
                  ? [0, 1] // Both Donor List (0) and Fundraising Bar (1) come before QR Code
                  : formData.enableDonorList
                    ? [0] // Only Donor List (0) comes before QR Code
                    : formData.showFundraisingBar
                      ? [0] // Only Fundraising Bar (0) comes before QR Code
                      : [], // No elements come before QR Code
              selectedValuesSoFar: [
                ...(formData.enableDonorList ? ['Donor List'] : []),
                ...(formData.showFundraisingBar ? ['Fundraising Bar'] : []),
                'QR Code',
              ],
              qrCodeType: 'HOSTED_PAGE',
              url: `${HOSTED_PAGE_URL}/donate/${formData.elementID}`,
              embedSize: '',
              goal: '',
              listType: '',
              donationsOffset: '',
              raisedOffset: '',
            },
          ],
    };

    // Conditionally add sourceWebsite and salesforceCampaignID if they have values (for ICNA Relief and other nonprofits)
    if (sourceWebsiteValues !== '') {
      payload.sourceWebsite = sourceWebsiteValues.toString();
    }

    if (salesforceCampaignIDICNAValues !== '') {
      payload.salesforceCampaignID = salesforceCampaignIDICNAValues.toString();
    }

    if (!createdForm) {
      payload.timeCreated = new Date().toISOString();
      payload.createdBy = getUserEmail() || 'unknown';
    }

    const url = `${import.meta.env.VITE_APP_FORMS_URL}`;

    try {
      console.log('[PUBLISH 1] API call starting, method:', createdForm ? 'PATCH' : 'POST');
      const result = await fetchWithAuthRetry(
        url,
        {
          method: createdForm ? 'PATCH' : 'POST',
          headers: {
            accept: '*/*',
            'content-type': 'application/json',
          },
          body: JSON.stringify(payload),
        },
        {
          context: 'Creating/updating fundraiser form',
          maxRetries: 3,
          retryDelay: 1000,
        },
      );

      console.log('[PUBLISH 2] API returned, ok:', result.ok);
      if (!result.ok) {
        console.error('[PUBLISH 2a] FAILED:', result.error);
        toast.error('Failed to save fundraiser. Please try again.');
        return;
      }

      const data = result.data;

      if (data.error) {
        console.error('[PUBLISH 2b] data.error:', data.error);
        toast.error(data.error);
        return;
      } else {
        console.log('[PUBLISH 3] Success, formID:', data.formID, '| elementID:', data.elementID);
        // NOTE: Image upload/delete moved AFTER onboarding transition (see [PUBLISH 7])
        // to prevent image failures from blocking the critical step change.
        if (formData.formID === '') {
          updateFormData('formID', data.formID);
          updateFormData('elementID', data.elementID);
          // Extract element IDs from element_type_to_id or fallback to direct properties
          const fundraisingBarID =
            data.element_type_to_id?.FUNDRAISING_BAR || data.fundraisingBarElementID;
          const donorListID = data.element_type_to_id?.DONOR_LIST || data.donorListElementID;
          if (fundraisingBarID) {
            updateFormData('fundraisingBarElementID', fundraisingBarID);
          }
          if (donorListID) {
            updateFormData('donorListElementID', donorListID);
          }
        }

        // Update totalTransactionAmount from response for fundraising bar display
        if (data.form?.totalTransactionAmount !== undefined) {
          updateFormData('totalTransactionAmount', data.form.totalTransactionAmount);
        }
        if (data.form?.subscriptionCount !== undefined) {
          updateFormData('subscriptionCount', data.form.subscriptionCount);
        }

        // Update donorListData from response for supporters list display
        if (data.donorListData) {
          updateFormData('donorListData', data.donorListData);
        }

        updateFormData('active', data.form?.active ?? data.active ?? payload.active);
        setCreatedForm(true);
        console.log('[PUBLISH 4] setCreatedForm(true) done');

        if (payload.active) trackDashboardPlg(posthog, DASHBOARD_PLG_EVENTS.FORM_PUBLISHED, {
          form_id: data.formID,
          form_type: formData.formType,
          creation_context: 'dashboard',
        });

        // ── Post-publish housekeeping (non-critical) ──────────────────────────
        // Each block wrapped in try-catch so failures don't show a misleading
        // "Failed to create fundraiser" toast — the form DID create successfully.

        // Image upload/delete — moved here from before the transition so a
        // network failure uploading an image can't block the step change.
        try {
          if (
            createdForm &&
            formData.imageWasUploaded &&
            !formData.image &&
            formData.resizedImage === null &&
            formData.elementID
          ) {
            console.log('[PUBLISH 7a] Deleting old image (post-transition)');
            await handleImageDelete(formData.elementID);
          }
          if (formData.resizedImage) {
            console.log('[PUBLISH 7b] Uploading image (post-transition)');
            if (formData.elementID != '' && formData.elementID != null) {
              await handleImageSubmit(formData.elementID);
            } else {
              await handleImageSubmit(data.elementID);
            }
          }
        } catch (err) {
          console.error('[CreateFundraiserDrawer] Image operation failed (non-critical):', err);
        }

        try {
          // Update email settings if configured
          if (formData.emailConfig.enableCustomEmail || formData.emailConfig.replyToAddress) {
            await changeEmailSettings();
          }
        } catch (err) {
          console.error(
            '[CreateFundraiserDrawer] Email settings update failed (non-critical):',
            err,
          );
        }

        const successMessage = payload.active ? 'Fundraiser saved successfully!' : 'Draft saved successfully!';
        toast.success(successMessage);
        setPreviewKey((k) => k + 1);

        try {
          // Refresh funds list to include any new fund names added in this form
          if (grabSelectableFunds) {
            grabSelectableFunds(await getAuthToken());
          }
        } catch (err) {
          console.error('[CreateFundraiserDrawer] Fund refresh failed (non-critical):', err);
        }

        // Backend response doesn't include salesforceCampaignID (stored in Elements table)
        // Manually enrich the response data with it from local state
        const enrichedData = {
          ...data,
          form: {
            ...(data.form || {}),
            active: data.form?.active ?? data.active ?? payload.active,
            salesforceCampaignID: salesforceCampaignIDICNAValues || undefined,
          },
        };

        // Call the parent callback to update the forms table
        if (props.onFormCreated) {
          props.onFormCreated(enrichedData);
        }

        // If we successfully created the form, update the elementID for image upload
        if (data.formID && !formData.elementID) {
          updateFormData('elementID', data.formID);
          // The ImageUploader component will automatically handle the upload
          // once elementID is set and image data is available
        }
      }
    } catch (error) {
      console.error('[PUBLISH CATCH] ❌ Outer catch fired! Error:', error);
      console.error('[PUBLISH CATCH] Stack:', error instanceof Error ? error.stack : 'no stack');
      toast.error('Failed to save fundraiser. Please try again.');
    } finally {
      console.log('[PUBLISH FINALLY] Done');
      setIsPublishing(false);
    }
  };

  const handleImageDelete = async (image_id: string) => {
    const result = await deleteImage({
      organizationId: props.merchant?.organizationId,
      imageName: `${merchantName}_${image_id}`,
      imageType: 'fundraiser',
      nonprofitName: merchantName,
      jwtToken: await getAuthToken(),
    });

    if (result.success) {
      updateFormData('image', null);
    } else {
      toast.error(result.error || 'Failed to delete image');
    }
  };

  const handleImageSubmit = async (image_id: string) => {
    // Check if there's an image to upload
    if (!formData.resizedImage) {
      return;
    }

    const result = await uploadImage({
      organizationId: props.merchant?.organizationId,
      imageData: formData.resizedImage,
      imageName: `${merchantName}_${image_id}`,
      imageType: 'fundraiser',
      nonprofitName: merchantName,
      jwtToken: await getAuthToken(),
    });

    if (result.success && result.imageUrl && result.imageVersion) {
      // The upload has already replaced the stored object, so its state below is
      // set either way. A version PATCH that still fails after its bounded retry
      // is reported here rather than thrown: thrown, it either vanished into the
      // save's "non-critical" catch or aborted the rest of the save.
      try {
        await persistFormImageVersion({
          formID: image_id,
          merchantName,
          imageVersion: result.imageVersion,
          jwtToken: await getAuthToken(),
        });
      } catch (error) {
        console.error('[image version] not saved after upload:', error);
        toast.error(error instanceof Error ? error.message : VERSION_NOT_SAVED_MESSAGE);
      }
      updateFormData('image', result.imageUrl);
      updateFormData('imageWasUploaded', true);
    } else {
      toast.error(result.error || 'Failed to upload image');
    }
  };

  const handleFormActivation = async (activeStatus: boolean) => {
    if (activeStatus && !paymentsReady) {
      toast.info('Enable payments before publishing this draft.');
      return;
    }
    setIsActivating(true);
    const payload = {
      merchantName: merchantName,
      elementID: formData.elementID,
      formID: formData.formID,
      active: activeStatus,
    };

    const requestOptions = {
      method: 'PATCH',
      headers: {
        accept: '*/*',
        'content-type': 'application/json',
        Authorization: await getAuthToken(),
      },
      body: JSON.stringify(payload),
    };

    const url = `${import.meta.env.VITE_APP_FORMS_URL}/toggle-active`;

    try {
      const result = await safeFetchJSON(url, requestOptions, {
        context: 'Toggling fundraiser active status',
        retries: 2,
        retryDelay: 1000,
      });

      if (!result.ok) {
        console.error('Failed to update form status:', result.error);
        toast.error('Failed to update form status. Please try again.');
        return;
      }

      const data = result.data;

      if (data.error) {
        toast.error(data.error);
        return;
      } else {
        updateFormData('active', data.active);
        if (props.onFormCreated) {
          props.onFormCreated(data);
        }
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update form status');
    } finally {
      setIsActivating(false);
    }
  };

  const sendToServerLatest = useLatestCallback(sendToServer);
  const handleFormActivationLatest = useLatestCallback(handleFormActivation);

  useEffect(() => {
    if (props.presentation !== 'workspace' || !props.setWorkspaceActions) return undefined;

    const actions: WorkspaceHeaderAction[] = [];

    if (formData.formID) {
      actions.push(
        {
          key: 'save',
          label: isPublishing ? 'Saving...' : 'Save',
          variant: 'primary' as const,
          disabled: isPublishing || isActivating,
          onClick: () => sendToServerLatest(),
        },
        {
          key: 'active',
          label: !formData.active
            ? isActivating
              ? 'Activating...'
              : 'Publish'
            : isActivating
              ? 'Deactivating...'
              : 'Deactivate',
          disabled: isPublishing || isActivating || (!formData.active && !paymentsReady),
          variant: !formData.active ? 'primary' : 'danger',
          onClick: () => handleFormActivationLatest(!formData.active),
        },
      );
    } else {
      actions.push({
        key: 'publish',
        label: isPublishing ? 'Saving...' : paymentsReady ? 'Publish' : 'Save draft',
        disabled: isPublishing || isActivating,
        variant: 'primary' as const,
        onClick: () => sendToServerLatest(),
      });
    }

    props.setWorkspaceActions(actions);

    return () => props.setWorkspaceActions?.(null);
    // Register current button state without depending on recreated local handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.presentation,
    props.setWorkspaceActions,
    paymentsReady,
    isPublishing,
    isActivating,
    formData.formID,
    formData.active,
    sendToServerLatest,
    handleFormActivationLatest,
  ]);

  const changeEmailSettings = async () => {
    const payload = {
      replyToEmailAddress: formData.emailConfig.replyToAddress,
      subject: formData.emailConfig.subject,
      emailMessage: formData.emailConfig.customMessage,
      requestType: 'FORMS_PAGE',
      formName: formData.formName,
      merchantName: merchantName,
    };

    const requestOptions = {
      method: 'POST',
      headers: {
        accept: '*/*',
        'content-type': 'application/json',
        Authorization: await getAuthToken(),
      },
      body: JSON.stringify(payload),
    };

    const deletePayload = {
      replyToEmailAddress: formData.emailConfig.replyToAddress,
      subject: formData.emailConfig.subject,
      emailMessage: formData.emailConfig.customMessage,
      requestType: 'FORMS_PAGE',
      formName: formData.formName,
      merchantName: merchantName,
    };

    const queryParams = Object.entries(deletePayload)
      .map(([key, value]) => `${key}=${encodeURIComponent(value as string)}`)
      .join('&');

    const requestOptionsDelete = {
      method: 'DELETE',
      headers: {
        accept: '*/*',
        'content-type': 'application/json',
        Authorization: await getAuthToken(),
      },
      body: JSON.stringify(deletePayload),
    };

    const url = import.meta.env.VITE_APP_EMAIL_CONFIG_URL;

    try {
      if (formData.emailConfig.enableCustomEmail === false) {
        const response = await fetch(url + '?' + queryParams, requestOptionsDelete);
        const data = await response.json();

        if (data.error) {
          toast.error(data.error);
          return;
        } else {
          if (grabSelectableFunds) {
            grabSelectableFunds(await getAuthToken());
          }
          toast.success('Email settings updated successfully');
        }
      }

      if (formData.emailConfig.enableCustomEmail === true) {
        const response = await fetch(url, requestOptions);
        const data = await response.json();

        if (data.error) {
          toast.error(data.error);
          return;
        } else {
          if (grabSelectableFunds) {
            grabSelectableFunds(await getAuthToken());
          }
          toast.success('Email settings updated successfully');
        }
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update email settings');
    }
  };

  // Clear states when modal is closed
  const clearStates = () => {
    props.setOpen(false);
    // Reset formData to initial state
    setFormData(initialFormData);

    // Reset UI states
    setCreatedForm(false);
    setEditorState(EditorState.createEmpty());
    setConvertedContent(null);
    setHasInitializedDefaults(false);
    setInvalidFields(new Set());
    setIsPublishing(false);
    setIsActivating(false);
    setActiveTab({
      id: 'form_configuration',
      name: 'Form Configuration',
      current: true,
    });
    setCurrentStep('appearance');
    setActivePreviewTab('Embed Form');
    setPreviewExpanded(false);

    // Reset sourceWebsite and salesforceCampaignID states
    setSourceWebsiteValues('');
    setSalesforceCampaignIDICNAValues('');

    // Note: salesforceCampaignID is intentionally cleared here
    // If form needs to persist between sessions, this should be handled by parent component
  };

  useEffect(() => {
    // Set merchantName from props
    if (props.merchant?.merchantName) {
      setMerchantName(props.merchant.merchantName);
    }

    // If no funds are available, call grabSelectableFunds to fetch them
    if (selectableFunds?.length === 0 && grabSelectableFunds) {
      (async () => {
        grabSelectableFunds(await getAuthToken());
      })();
    }
  }, [selectableFunds, grabSelectableFunds, props.merchant]);

  // Pre-fill the form color from the org's brand color when the drawer opens.
  // Only applies when: (a) drawer is open, (b) org has a brand color set, and
  // (c) the color field is still at the system default — meaning the user hasn't
  // picked a different color during this session.
  // clearStates() resets color to COLORS.FORM_DEFAULT_COLOR on close, so
  // re-opening the drawer correctly re-applies the org's brand color.
  useEffect(() => {
    if (!props.open) return;
    const orgBrandColor = props.merchant?.brandColor;
    if (!orgBrandColor) return;
    setFormData((prev) => {
      if (prev.color !== COLORS.FORM_DEFAULT_COLOR) return prev; // user already changed it
      return { ...prev, color: orgBrandColor as (typeof initialFormData)['color'] };
    });
  }, [props.open, props.merchant]);

  // Fetch Double the Donation API key when drawer opens
  // Note: Must re-fetch on props.open because clearStates() resets formData when drawer closes
  useEffect(() => {
    const myFunction = async () => {
      const organizationId = props.merchant?.organizationId;
      const nonprofit = props.merchant?.merchantName || '';
      let auth = await getAuthToken();
      // Only fetch when drawer is open and merchant is set
      if (props.merchant !== null && props.open) {
        if (auth === '') {
          auth = await getAuthToken();
        }

        // grab double the donation api key if it exists
        const response = await createDoubleTheDonationClient({ organizationId, authorization: auth }).getApiKeys({
          nonprofit,
        });
        const responseData = response.body;
        updateFormData('dtdPublicApiKey', typeof responseData === 'object' && 'publicApiKey' in responseData ? responseData.publicApiKey : undefined);
      }
    };
    myFunction();
  }, [props.merchant, props.open]);

  useEffect(() => {
    setTimeout(() => {
      ReactTooltip.rebuild();
    }, 100);
  }, [props.open]);

  // Populate sourceWebsite and salesforceCampaignID options from existing forms (for ICNA Relief)
  useEffect(() => {
    if (props.merchant && props.merchant.merchantName) {
      const merchantName = props.merchant.merchantName;
      if (
        (merchantName === 'ICNA Relief USA' || merchantName === 'ICNA Relief USA Dev') &&
        props.selectableForms
      ) {
        const salesforceCampaignID: string[] = [];
        const sourceWebsiteList: string[] = [];

        if (Array.isArray(props.selectableForms)) {
          props.selectableForms.forEach((form) => {
            // Check if the form has campaignID and it's not empty
            if (
              typeof form.salesforceCampaignID === 'string' &&
              form.salesforceCampaignID.trim() !== ''
            ) {
              if (!salesforceCampaignID.includes(form.salesforceCampaignID.trim())) {
                salesforceCampaignID.push(form.salesforceCampaignID.trim());
              }
            }
            if (typeof form.sourceWebsite === 'string' && form.sourceWebsite.trim() !== '') {
              if (!sourceWebsiteList.includes(form.sourceWebsite.trim())) {
                sourceWebsiteList.push(form.sourceWebsite.trim());
              }
            }
          });
        }

        // Set the campaignIDOptions and sourceWebsite options in state
        setSalesforceCampaignIDICNA(salesforceCampaignID);
        setSourceWebsite(sourceWebsiteList);
      }

      if (props.merchant['bloomerangConnectionEnabled']) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setBloomerangAppeals((props.merchant['bloomerangAppeals'] as any[]) || []);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setBloomerangCampaigns((props.merchant['bloomerangCampaigns'] as any[]) || []);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setBloomerangFunds((props.merchant['bloomerangFunds'] as any[]) || []);
      }

      if (props.merchant['virtuousConnectionEnabled']) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setVirtuousProjects((props.merchant['virtuousProjects'] as any[]) || []);
      }
    }
  }, [props.merchant, props.selectableForms]);

  // Initialize default items only when sheet opens (not when switching tabs)
  useEffect(() => {
    if (props.open && !hasInitializedDefaults) {
      setHasInitializedDefaults(true);
    }

    // Reset initialization flag when sheet is closed so it can re-initialize on next open
    if (!props.open && hasInitializedDefaults) {
      setHasInitializedDefaults(false);
    }
  }, [props.open, hasInitializedDefaults]);

  // Pre-populate form data when duplicating an existing form
  useEffect(() => {
    if (!props.open || !props.duplicateFormData) return;

    const form = props.duplicateFormData;

    // Helper to convert backend frequency format to dashboard format
    const toDashboardFrequency = (freq: string): string => {
      const mapping: Record<string, string> = {
        ONE_TIME: 'One-Time',
        DAILY: 'Daily',
        WEEKLY: 'Weekly',
        MONTHLY: 'Monthly',
        ANNUALLY: 'Annually',
      };
      return mapping[freq] || freq;
    };

    // Build frequency amounts from the source form's amount arrays
    const newFrequencyAmounts = { ...initialFormData.frequencyAmounts };

    const formatDefaultAmount = (value: number | null | undefined): string => {
      return value && value > 0 ? value.toString() : '';
    };

    if (form.oneTimeAmounts) {
      newFrequencyAmounts['One-Time'] = {
        amount1: form.oneTimeAmounts[0]?.toString() || '',
        amount2: form.oneTimeAmounts[1]?.toString() || '',
        amount3: form.oneTimeAmounts[2]?.toString() || '',
        amount4: form.oneTimeAmounts[3]?.toString() || '',
        defaultAmount: formatDefaultAmount(form.defaultOneTimeAmount),
      };
    }
    if (form.dailyAmounts) {
      newFrequencyAmounts['Daily'] = {
        amount1: form.dailyAmounts[0]?.toString() || '',
        amount2: form.dailyAmounts[1]?.toString() || '',
        amount3: form.dailyAmounts[2]?.toString() || '',
        amount4: form.dailyAmounts[3]?.toString() || '',
        defaultAmount: formatDefaultAmount(form.defaultDailyAmount),
      };
    }
    if (form.weeklyAmounts) {
      newFrequencyAmounts['Weekly'] = {
        amount1: form.weeklyAmounts[0]?.toString() || '',
        amount2: form.weeklyAmounts[1]?.toString() || '',
        amount3: form.weeklyAmounts[2]?.toString() || '',
        amount4: form.weeklyAmounts[3]?.toString() || '',
        defaultAmount: formatDefaultAmount(form.defaultWeeklyAmount),
      };
    }
    if (form.monthlyAmounts) {
      newFrequencyAmounts['Monthly'] = {
        amount1: form.monthlyAmounts[0]?.toString() || '',
        amount2: form.monthlyAmounts[1]?.toString() || '',
        amount3: form.monthlyAmounts[2]?.toString() || '',
        amount4: form.monthlyAmounts[3]?.toString() || '',
        defaultAmount: formatDefaultAmount(form.defaultMonthlyAmount),
      };
    }
    if (form.annuallyAmounts) {
      newFrequencyAmounts['Annually'] = {
        amount1: form.annuallyAmounts[0]?.toString() || '',
        amount2: form.annuallyAmounts[1]?.toString() || '',
        amount3: form.annuallyAmounts[2]?.toString() || '',
        amount4: form.annuallyAmounts[3]?.toString() || '',
        defaultAmount: formatDefaultAmount(form.defaultAnnuallyAmount),
      };
    }

    // Convert frequencies from backend format
    const convertedFrequencies =
      form.frequencies && Array.isArray(form.frequencies)
        ? form.frequencies.map(toDashboardFrequency)
        : ['One-Time'];

    const convertedDefaultFrequency = form.defaultFrequency
      ? toDashboardFrequency(form.defaultFrequency)
      : 'One-Time';

    // Get first recurring frequency for schedule tab
    const frequencyOrder = ['Daily', 'Weekly', 'Monthly', 'Annually'];
    const firstRecurring = frequencyOrder.find((f) => convertedFrequencies.includes(f)) || '';

    // Normalize scheduled range data
    const defaultScheduleConfig = {
      enabled: false,
      scheduleType: 'immediate',
      startDateOffset: null,
      endDateOffset: null,
      scheduledTime: '',
    };
    let normalizedScheduledRanges = {
      Daily: { ...defaultScheduleConfig },
      Weekly: { ...defaultScheduleConfig },
      Monthly: { ...defaultScheduleConfig },
      Annually: { ...defaultScheduleConfig },
    } as FrequencyScheduledRangeMap;

    if (form.defaultScheduledRange) {
      const data = form.defaultScheduledRange;
      if (data.Daily || data.Weekly || data.Monthly || data.Annually) {
        normalizedScheduledRanges = {
          Daily: data.Daily ? { ...data.Daily } : { ...defaultScheduleConfig },
          Weekly: data.Weekly ? { ...data.Weekly } : { ...defaultScheduleConfig },
          Monthly: data.Monthly ? { ...data.Monthly } : { ...defaultScheduleConfig },
          Annually: data.Annually ? { ...data.Annually } : { ...defaultScheduleConfig },
        };
      }
    }

    // Set all form data — spread initialFormData for type completeness, then override with source form values
    setFormData({
      ...initialFormData,
      formName: `${form.formName || ''} Copy`,
      formID: '',
      elementID: '',
      fundraisingBarElementID: '',
      donorListElementID: '',
      active: true,
      formType: form.formType || 'EMBED_FORM',
      fundraiserType: form.fundraiserType || 'DONATION_FORM',
      color: form.lightColor || COLORS.FORM_DEFAULT_COLOR,
      image: null,
      resizedImage: null,
      imageWasUploaded: false,
      targetAmount: form.targetAmount?.toString() || '',
      amountOffset: form.amountOffset?.toString() || '',
      countOffset: form.countOffset?.toString() || '',
      trackingMetric: form.trackingMetric || 'DOLLAR_AMOUNT',
      metricLabel: form.metricLabel || 'Sustainers',
      subscriptionCount: 0,
      totalTransactionAmount: 0,
      showFundraisingBar: form.showFundraisingBar || false,
      enableDonorList: form.enableDonorList !== undefined ? form.enableDonorList : true,
      donorListType: form.donorListType || 'RECENT_DONORS',
      showAllSupportersAnonymous: form.showAllSupportersAnonymous ?? false,
      showProductGroups: false,
      enableEmbedForm: form.enableEmbedForm ?? true,
      defaultDonationType: convertedDefaultFrequency,
      checkedValues: convertedFrequencies,
      frequencyAmounts: newFrequencyAmounts,
      requireScheduleDonations: form.showScheduleDonation || false,
      frequencyScheduledRanges: normalizedScheduledRanges,
      activeScheduleTab: firstRecurring,
      showAnonymousDonations: form.showAnonymousDonations || false,
      showOrganizationNameField: form.showOrganizationNameField || false,
      enablePhoneNumber: form.phoneNumber ?? false,
      enableBillingAddress: form.showAddress ?? false,
      enableDoubleTheDonation: form.showDoubleTheDonation ?? false,
      selectedCountry: form.defaultCountry || 'Choose Default Country',
      enableCommunicationConsent: form.enableCommunicationConsent ?? false,
      communicationConsentMessage: form.communicationConsentMessage || '',
      enableDedication: form.enableDedication ?? false,
      dedicationTypes: form.dedicationTypes || ['In Honor Of', 'In Memory Of'],
      enableDedicationMessage: form.enableDedicationMessage ?? false,
      funds: form.funds || [],
      customInputs: form.customInputs && Array.isArray(form.customInputs) ? form.customInputs : [],
      faqs: form.faqs && Array.isArray(form.faqs) ? form.faqs : [],
      crowdfundingOptions:
        form.crowdfundingOptions && Array.isArray(form.crowdfundingOptions)
          ? form.crowdfundingOptions
          : [],
      productGroups:
        form.productGroups && Array.isArray(form.productGroups) ? form.productGroups : [],
      oneTimeCrowdfundingOptions: form.oneTimeCrowdfundingOptions || [],
      dailyCrowdfundingOptions: form.dailyCrowdfundingOptions || [],
      weeklyCrowdfundingOptions: form.weeklyCrowdfundingOptions || [],
      monthlyCrowdfundingOptions: form.monthlyCrowdfundingOptions || [],
      annuallyCrowdfundingOptions: form.annuallyCrowdfundingOptions || [],
      oneTimeProductGroups: form.oneTimeProductGroups || [],
      dailyProductGroups: form.dailyProductGroups || [],
      weeklyProductGroups: form.weeklyProductGroups || [],
      monthlyProductGroups: form.monthlyProductGroups || [],
      annuallyProductGroups: form.annuallyProductGroups || [],
      defaultOneTimeCrowdfundingOption: form.defaultOneTimeCrowdfundingOption,
      defaultDailyCrowdfundingOption: form.defaultDailyCrowdfundingOption,
      defaultWeeklyCrowdfundingOption: form.defaultWeeklyCrowdfundingOption,
      defaultMonthlyCrowdfundingOption: form.defaultMonthlyCrowdfundingOption,
      defaultAnnuallyCrowdfundingOption: form.defaultAnnuallyCrowdfundingOption,
      details: form.details || '',
      description: form.details || '',
      fieldMappings: form.fieldMappings || [],
      donorListData: null,
      emailConfig: {
        enableCustomEmail: false,
        replyToAddress: '',
        subject: '',
        customMessage: '',
      },
    });

    // Set merchant name
    if (form.nonprofitName) {
      setMerchantName(form.nonprofitName);
    }

    // Set Salesforce Campaign ID if present
    if (form.salesforceCampaignID) {
      setSalesforceCampaignIDICNAValues(form.salesforceCampaignID);
    }

    // Populate rich text editor if details exist
    if (form.details) {
      try {
        const contentBlock = htmlToDraft(form.details);
        if (contentBlock) {
          const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
          setEditorState(EditorState.createWithContent(contentState));
          setConvertedContent(form.details);
        }
      } catch {
        // If HTML parsing fails, just set the text content
        setConvertedContent(form.details);
      }
    }

    // Fetch email config from the source form so it can be pre-populated for the duplicate
    const loadSourceEmailConfig = async () => {
      if (!form.formName) return;
      try {
        const requestOptions = {
          method: 'GET',
          headers: {
            accept: '*/*',
            'content-type': 'application/json',
            Authorization: await getAuthToken(),
          },
        };
        const params = new URLSearchParams();
        params.append('requestType', 'FORMS_PAGE');
        params.append('formName', form.formName);
        const url = import.meta.env.VITE_APP_EMAIL_CONFIG_URL;
        const response = await fetch(url + `?${params.toString()}`, requestOptions);
        if (!response.ok) return;
        const emailData = await response.json();
        if (emailData && emailData.message !== 'Unauthorized') {
          setFormData((prev) => ({
            ...prev,
            emailConfig: {
              enableCustomEmail: true,
              replyToAddress: emailData.replyToEmail || '',
              subject: emailData.emailSubject || '',
              customMessage: emailData.emailMessage || '',
            },
          }));
        }
      } catch (error) {
        console.error('Failed to load source form email configuration:', error);
      }
    };
    loadSourceEmailConfig();
  }, [props.open, props.duplicateFormData]);

  useEffect(() => {
    setTimeout(() => {
      ReactTooltip.rebuild();
    }, 100);
  }, [props.open]);

  useEffect(() => {
    const rawContentState = convertToRaw(editorState.getCurrentContent());
    let markup = draftToHtml(rawContentState);
    // if markup is empty, set it to an empty string
    if (markup.trim() === '<p></p>') {
      markup = '';
    }
    // replace all <p></p> with <br>
    markup = markup.replace(/<p><\/p>/g, '<br>');
    setConvertedContent(markup);
  }, [editorState]);

  // Memoize frequency amounts for real-time preview updates
  const previewOneTimeAmounts = React.useMemo(
    () =>
      formData.frequencyAmounts['One-Time']
        ? [
            formData.frequencyAmounts['One-Time'].amount1,
            formData.frequencyAmounts['One-Time'].amount2,
            formData.frequencyAmounts['One-Time'].amount3,
            formData.frequencyAmounts['One-Time'].amount4,
          ]
            .filter((amount) => amount && amount.trim())
            .map((amount) => parseFloat(amount))
        : [25, 50, 100, 250],
    [formData.frequencyAmounts],
  );

  const previewDailyAmounts = React.useMemo(
    () =>
      formData.frequencyAmounts['Daily']
        ? [
            formData.frequencyAmounts['Daily'].amount1,
            formData.frequencyAmounts['Daily'].amount2,
            formData.frequencyAmounts['Daily'].amount3,
            formData.frequencyAmounts['Daily'].amount4,
          ]
            .filter((amount) => amount && amount.trim())
            .map((amount) => parseFloat(amount))
        : [],
    [formData.frequencyAmounts],
  );

  const previewWeeklyAmounts = React.useMemo(
    () =>
      formData.frequencyAmounts['Weekly']
        ? [
            formData.frequencyAmounts['Weekly'].amount1,
            formData.frequencyAmounts['Weekly'].amount2,
            formData.frequencyAmounts['Weekly'].amount3,
            formData.frequencyAmounts['Weekly'].amount4,
          ]
            .filter((amount) => amount && amount.trim())
            .map((amount) => parseFloat(amount))
        : [],
    [formData.frequencyAmounts],
  );

  const previewMonthlyAmounts = React.useMemo(
    () =>
      formData.frequencyAmounts['Monthly']
        ? [
            formData.frequencyAmounts['Monthly'].amount1,
            formData.frequencyAmounts['Monthly'].amount2,
            formData.frequencyAmounts['Monthly'].amount3,
            formData.frequencyAmounts['Monthly'].amount4,
          ]
            .filter((amount) => amount && amount.trim())
            .map((amount) => parseFloat(amount))
        : [],
    [formData.frequencyAmounts],
  );

  const previewAnnuallyAmounts = React.useMemo(
    () =>
      formData.frequencyAmounts['Annually']
        ? [
            formData.frequencyAmounts['Annually'].amount1,
            formData.frequencyAmounts['Annually'].amount2,
            formData.frequencyAmounts['Annually'].amount3,
            formData.frequencyAmounts['Annually'].amount4,
          ]
            .filter((amount) => amount && amount.trim())
            .map((amount) => parseFloat(amount))
        : [],
    [formData.frequencyAmounts],
  );

  const previewDefaultOneTimeAmount = React.useMemo(
    () =>
      formData.frequencyAmounts['One-Time']?.defaultAmount
        ? parseInt(formData.frequencyAmounts['One-Time'].defaultAmount)
        : 0,
    [formData.frequencyAmounts],
  );

  const previewDefaultDailyAmount = React.useMemo(
    () =>
      formData.frequencyAmounts['Daily']?.defaultAmount
        ? parseInt(formData.frequencyAmounts['Daily'].defaultAmount)
        : 0,
    [formData.frequencyAmounts],
  );

  const previewDefaultWeeklyAmount = React.useMemo(
    () =>
      formData.frequencyAmounts['Weekly']?.defaultAmount
        ? parseInt(formData.frequencyAmounts['Weekly'].defaultAmount)
        : 0,
    [formData.frequencyAmounts],
  );

  const previewDefaultMonthlyAmount = React.useMemo(
    () =>
      formData.frequencyAmounts['Monthly']?.defaultAmount
        ? parseInt(formData.frequencyAmounts['Monthly'].defaultAmount)
        : 0,
    [formData.frequencyAmounts],
  );

  const previewDefaultAnnuallyAmount = React.useMemo(
    () =>
      formData.frequencyAmounts['Annually']?.defaultAmount
        ? parseInt(formData.frequencyAmounts['Annually'].defaultAmount)
        : 0,
    [formData.frequencyAmounts],
  );

  return (
    <Sheet
      embedded={props.presentation === 'workspace'}
      open={props.open}
      onOpenChange={(open) => {
        if (!open) {
          clearStates();
        }
        props.setOpen(open);
      }}
    >
      <SheetContent
        embedded={props.presentation === 'workspace'}
        side="bottom"
        className={
          props.presentation === 'workspace'
            ? 'h-auto overflow-visible rounded-none bg-transparent p-0'
            : 'pointer-events-none h-[95vh] overflow-hidden rounded-t-[10px] bg-white'
        }
      >
        <div
          className={`pointer-events-none mx-auto flex w-full max-w-[1600px] flex-col ${
            props.presentation === 'workspace' ? 'h-auto px-3' : 'h-full'
          }`}
        >
          {props.presentation !== 'workspace' && (
            <header className="pointer-events-auto shrink-0 border-b border-paper-inset bg-white pb-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-start space-x-2">
                  <div className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[19px] border border-edge bg-paper-inset">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5.625 3.75C4.17525 3.75 3 4.92525 3 6.375C3 7.82475 4.17525 9 5.625 9H18.375C19.8247 9 21 7.82475 21 6.375C21 4.92525 19.8247 3.75 18.375 3.75H5.625Z"
                        fill="#6B6458"
                      />
                      <path
                        d="M3.75 11.25C3.33579 11.25 3 11.5858 3 12C3 12.4142 3.33579 12.75 3.75 12.75H20.25C20.6642 12.75 21 12.4142 21 12C21 11.5858 20.6642 11.25 20.25 11.25H3.75Z"
                        fill="#6B6458"
                      />
                      <path
                        d="M3 15.75C3 15.3358 3.33579 15 3.75 15H20.25C20.6642 15 21 15.3358 21 15.75C21 16.1642 20.6642 16.5 20.25 16.5H3.75C3.33579 16.5 3 16.1642 3 15.75Z"
                        fill="#6B6458"
                      />
                      <path
                        d="M3.75 18.75C3.33579 18.75 3 19.0858 3 19.5C3 19.9142 3.33579 20.25 3.75 20.25H20.25C20.6642 20.25 21 19.9142 21 19.5C21 19.0858 20.6642 18.75 20.25 18.75H3.75Z"
                        fill="#6B6458"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-base font-bold text-ink-muted">Create Fundraiser</p>
                    <p className="text-sm text-ink-muted">
                      Customize the form by inputting additional details below
                    </p>
                  </div>
                </div>
                <div className="flex flex-row flex-wrap items-center gap-2 sm:justify-end">
                  {(
                    <Button
                      variant="ghost"
                      onClick={() => {
                        props.setOpen(false);
                        clearStates();
                      }}
                    >
                      Close
                    </Button>
                  )}
                  {/* Archive Button */}

                  {formData.formID && (
                    <Button
                      onClick={() => sendToServer()}
                      disabled={isPublishing || isActivating}
                      variant="primary"
                      size="workspace"
                    >
                      {isPublishing ? 'Saving...' : 'Save'}
                    </Button>
                  )}

                  {/* Activate/Deactivate Button */}
                  {formData.formID && (
                    <Button
                      onClick={() => handleFormActivation(!formData.active)}
                      disabled={isActivating || isPublishing || (!formData.active && !paymentsReady)}
                      variant={!formData.active ? 'primary' : 'danger'}
                      size="workspace"
                    >
                      {!formData.active
                        ? isActivating
                          ? 'Activating...'
                          : 'Publish'
                        : isActivating
                          ? 'Deactivating...'
                          : 'Deactivate'}
                    </Button>
                  )}

                  {/* Publish Button - Now handles save and publish */}
                  {!formData.formID && (
                    <div
                      className="flex flex-col items-start gap-1 sm:items-end"
                    >
                      <Button
                        onClick={() => sendToServer()}
                        variant="primary"
                        size="workspace"
                        disabled={isPublishing || isActivating}
                      >
                        {isPublishing ? 'Saving...' : paymentsReady ? 'Publish' : 'Save draft'}
                      </Button>

                    </div>
                  )}
                </div>
              </div>
            </header>
          )}

          {/* Main Content - Split Layout (non-clickable during onboarding tour so only popover Next/Prev navigate) */}
          {props.presentation === 'workspace' && (
            <FormEditorSectionJump
              configurationRef={configurationSectionRef}
              previewRef={previewSectionRef}
            />
          )}

          <ScrollFade
            containerClassName={
              props.presentation === 'workspace' ? 'overflow-visible' : 'min-h-0 flex-1'
            }
            className={getFormEditorLayoutClassName({
              workspace: props.presentation === 'workspace',
              expanded: previewExpanded,
              interactionClassName:
                'pointer-events-auto',
            })}
            fadeClassName="rounded-b-md"
            showTopFade={props.presentation !== 'workspace'}
            topFadeClassName="rounded-t-md"
            showScrollIndicator={props.presentation !== 'workspace'}
            scrollIndicatorOffset={props.presentation === 'workspace' ? '-10px' : '0px'}
          >
            {/* Left Side - Preview Section */}

            <div
              ref={previewSectionRef}
              role={props.presentation === 'workspace' ? 'region' : undefined}
              tabIndex={props.presentation === 'workspace' ? -1 : undefined}
              aria-label={props.presentation === 'workspace' ? 'Form preview' : undefined}
              className={getFormEditorPreviewColumnClassName({
                workspace: props.presentation === 'workspace',
                minHeightClassName: formData.elementID
                  ? activePreviewTab === 'Embed Form'
                    ? 'min-h-[600px]'
                    : ''
                  : activePreviewTab === 'Embed Form'
                    ? 'min-h-[400px]'
                    : '',
              })}
            >
              {/* Preview */}
              <div
                className={getFormEditorPreviewFrameClassName({
                  workspace: props.presentation === 'workspace',
                  sticky: !formData.elementID,
                })}
              >
                <FundraiserFormPreview
                  key={previewKey}
                  formName={formData.formName}
                  image={formData.image}
                  color={formData.color}
                  convertedContent={
                    convertedContent ||
                    (formData.description
                      ? `<p>${formData.description.replace(/\n/g, '</p><p>')}</p>`
                      : null)
                  }
                  customTickets={[]}
                  customFAQs={formData.faqs}
                  customInputs={formData.customInputs}
                  activePreviewTab={activePreviewTab}
                  setActivePreviewTab={setActivePreviewTab}
                  merchantName={props.merchant?.merchantName}
                  elementID={formData.elementID || 'NEW_FORM'}
                  eventDate=""
                  eventStartTime=""
                  eventEndTime=""
                  eventLocation=""
                  defaultColor={COLORS.FORM_DEFAULT_COLOR}
                  showFundraisingBar={formData.showFundraisingBar}
                  targetAmount={formData.targetAmount}
                  amountOffset={formData.amountOffset}
                  countOffset={formData.countOffset}
                  trackingMetric={formData.trackingMetric}
                  metricLabel={formData.metricLabel}
                  subscriptionCount={formData.subscriptionCount ?? 0}
                  totalTransactionAmount={formData.totalTransactionAmount}
                  enableEmbedForm={formData.enableEmbedForm}
                  showAnonymousDonations={formData.showAnonymousDonations}
                  showOrganizationNameField={formData.showOrganizationNameField}
                  active={formData.active}
                  funds={formData.funds}
                  frequencies={formData.checkedValues}
                  oneTimeAmounts={previewOneTimeAmounts}
                  dailyAmounts={previewDailyAmounts}
                  weeklyAmounts={previewWeeklyAmounts}
                  monthlyAmounts={previewMonthlyAmounts}
                  annuallyAmounts={previewAnnuallyAmounts}
                  defaultFrequency={formData.defaultDonationType}
                  defaultOneTimeAmount={previewDefaultOneTimeAmount}
                  defaultDailyAmount={previewDefaultDailyAmount}
                  defaultWeeklyAmount={previewDefaultWeeklyAmount}
                  defaultMonthlyAmount={previewDefaultMonthlyAmount}
                  defaultAnnuallyAmount={previewDefaultAnnuallyAmount}
                  formType={formData.formType}
                  crowdfundingOptions={formData.crowdfundingOptions}
                  productGroups={formData.productGroups}
                  oneTimeCrowdfundingOptions={formData.oneTimeCrowdfundingOptions}
                  dailyCrowdfundingOptions={formData.dailyCrowdfundingOptions}
                  weeklyCrowdfundingOptions={formData.weeklyCrowdfundingOptions}
                  monthlyCrowdfundingOptions={formData.monthlyCrowdfundingOptions}
                  annuallyCrowdfundingOptions={formData.annuallyCrowdfundingOptions}
                  oneTimeProductGroups={formData.oneTimeProductGroups}
                  dailyProductGroups={formData.dailyProductGroups}
                  weeklyProductGroups={formData.weeklyProductGroups}
                  monthlyProductGroups={formData.monthlyProductGroups}
                  annuallyProductGroups={formData.annuallyProductGroups}
                  enableDonorList={formData.enableDonorList}
                  donorListType={formData.donorListType}
                  donorListData={formData.donorListData}
                  showAllSupportersAnonymous={formData.showAllSupportersAnonymous}
                  fundraisingBarElementID={formData.fundraisingBarElementID}
                  donorListElementID={formData.donorListElementID}
                  showScheduleDonation={formData.requireScheduleDonations}
                  defaultScheduledRange={
                    formData.requireScheduleDonations
                      ? formData.frequencyScheduledRanges
                      : undefined
                  }
                  studioMode={props.presentation === 'workspace'}
                  isExpanded={previewExpanded}
                  onToggleExpanded={
                    props.presentation === 'workspace'
                      ? () => setPreviewExpanded((expanded) => !expanded)
                      : undefined
                  }
                />
              </div>

              {formData.elementID && (
                <div className="rounded-lg bg-white">
                  {(!paymentsReady || formData.active !== true) ? (
                    <div className="rounded-md border border-edge bg-paper-inset px-4 py-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-4 h-4 mt-0.5 border-2 border-sage border-t-transparent rounded-full animate-spin" />
                        <div>
                          <p className="text-sm font-medium text-ink">
                            {paymentsReady ? 'Publish your draft to share it' : 'Enable payments to publish and share'}
                          </p>
                          <p className="text-xs text-ink-muted mt-1">
                            You can save and preview your work. Links, QR codes, and embed code are available after payments are enabled and you publish.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <FormSharingCards
                        elementID={formData.elementID}
                        formType="donate"
                        embedEntity="EMBED_FORM"
                        hostedPageUrl={`${HOSTED_PAGE_URL}/donate/${formData.elementID}`}
                        websiteUrl={
                          (props.merchant?.organizationBaseURLs as string[] | undefined)?.[0]
                        }
                        color={formData.color}
                        onShowQR={() => setShowQRModal(true)}
                        hasQRCode={!!savedQRCodeData}
                      />
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Right Side - Form Configuration */}
            <div
              ref={configurationSectionRef}
              role={props.presentation === 'workspace' ? 'region' : undefined}
              tabIndex={props.presentation === 'workspace' ? -1 : undefined}
              aria-label={props.presentation === 'workspace' ? 'Form editor' : undefined}
              className={getFormEditorConfigurationColumnClassName({
                workspace: props.presentation === 'workspace',
              })}
            >
              {/* Form Tabs */}
              <div className="border-edge bg-white">
                <nav
                  className="-mb-px flex space-x-6 overflow-x-auto border-b pb-2 md:overflow-x-hidden md:pb-0 pt-2"
                  aria-label="Tabs"
                >
                  {tabs.map((tab) => {
                    const hasErrors = getTabValidationErrors(tab.name);
                    return (
                      <button
                        type="button"
                        key={tab.id}
                        onClick={() => setActiveTab(tab)}
                        className={classNames(
                          tab.name === activeTab.name
                            ? hasErrors
                              ? 'border-red-500 text-red-600'
                              : 'border-ink-secondary text-ink-secondary'
                            : hasErrors
                              ? 'border-transparent text-red-500 hover:text-red-600'
                              : 'border-transparent text-ink-muted hover:text-ink-secondary',
                          'whitespace-nowrap px-1 pb-1.5 text-[13px] font-medium border-b-2 border-x-0 border-t-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0',
                        )}
                      >
                        {tab.name}
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Form Content */}
              <div className="flex-1 lg:overflow-visible py-4">
                {/* Form Configuration Tab Content */}
                {activeTab.name === 'Form Configuration' && (
                  <FundraiserFormConfigurationTab
                    workspacePresentation={props.presentation === 'workspace'}
                    currentStep={currentStep}
                    setCurrentStep={setCurrentStep}
                    handleContinue={handleContinue}
                    handleBack={handleBack}
                    // Validation props
                    setInvalidFields={setInvalidFields}
                    handleFieldFocus={handleFieldFocus}
                    isFieldInvalid={isFieldInvalid}
                    invalidFields={invalidFields}
                    // Form data and handler
                    fundraiserName={formData.formName}
                    setFundraiserName={(value) => updateFormData('formName', value)}
                    color={formData.color}
                    setColor={(value) => updateFormData('color', value)}
                    targetAmount={formData.targetAmount}
                    setTargetAmount={(value) => updateFormData('targetAmount', value)}
                    enableDonorList={formData.enableDonorList}
                    donorListType={formData.donorListType}
                    setDonorListType={(value) => updateFormData('donorListType', value)}
                    setEnableDonorList={(value) => updateFormData('enableDonorList', value)}
                    showAllSupportersAnonymous={formData.showAllSupportersAnonymous}
                    setShowAllSupportersAnonymous={(value) =>
                      updateFormData('showAllSupportersAnonymous', value)
                    }
                    showFundraisingBar={formData.showFundraisingBar}
                    setShowFundraisingBar={(value) => updateFormData('showFundraisingBar', value)}
                    amountOffset={formData.amountOffset}
                    setAmountOffset={(value) => updateFormData('amountOffset', value)}
                    trackingMetric={formData.trackingMetric}
                    setTrackingMetric={(value) => updateFormData('trackingMetric', value)}
                    metricLabel={formData.metricLabel}
                    setMetricLabel={(value) => updateFormData('metricLabel', value)}
                    countOffset={formData.countOffset}
                    setCountOffset={(value) => updateFormData('countOffset', value)}
                    merchantName={
                      props.merchant?.merchantName ||
                      (typeof props.merchant === 'string' ? props.merchant : undefined)
                    }
                    elementID={formData.formID}
                    image={formData.image}
                    setImage={(value) => updateFormData('image', value)}
                    setResizedImage={(value) => updateFormData('resizedImage', value)}
                    // Description props
                    description={formData.description}
                    setDescription={(value) => updateFormData('description', value)}
                    // Fund selection props
                    funds={formData.funds}
                    setFunds={(value) => updateFormData('funds', value)}
                    selectableFunds={selectableFunds}
                    // Frequency props
                    checkedValues={formData.checkedValues}
                    setCheckedValues={handleCheckedValuesUpdate}
                    defaultDonationType={formData.defaultDonationType}
                    setDefaultDonationType={(value) => updateFormData('defaultDonationType', value)}
                    requireScheduleDonations={formData.requireScheduleDonations}
                    setRequireScheduleDonations={(value: boolean) =>
                      updateFormData('requireScheduleDonations', value)
                    }
                    frequencyScheduledRanges={formData.frequencyScheduledRanges}
                    setFrequencyScheduledRanges={(value: FrequencyScheduledRangeMap) =>
                      updateFormData('frequencyScheduledRanges', value)
                    }
                    activeScheduleTab={formData.activeScheduleTab}
                    setActiveScheduleTab={(value: string) =>
                      updateFormData('activeScheduleTab', value)
                    }
                    // Amount props
                    frequencyAmounts={formData.frequencyAmounts}
                    setFrequencyAmounts={(value) => updateFormData('frequencyAmounts', value)}
                    // Crowdfunding Options props (Legacy)
                    crowdfundingOptions={formData.crowdfundingOptions}
                    setCrowdfundingOptions={(value) => updateFormData('crowdfundingOptions', value)}
                    // Per-frequency Crowdfunding Options props (NEW)
                    oneTimeCrowdfundingOptions={formData.oneTimeCrowdfundingOptions}
                    setOneTimeCrowdfundingOptions={(value) =>
                      updateFormData('oneTimeCrowdfundingOptions', value)
                    }
                    dailyCrowdfundingOptions={formData.dailyCrowdfundingOptions}
                    setDailyCrowdfundingOptions={(value) =>
                      updateFormData('dailyCrowdfundingOptions', value)
                    }
                    weeklyCrowdfundingOptions={formData.weeklyCrowdfundingOptions}
                    setWeeklyCrowdfundingOptions={(value) =>
                      updateFormData('weeklyCrowdfundingOptions', value)
                    }
                    monthlyCrowdfundingOptions={formData.monthlyCrowdfundingOptions}
                    setMonthlyCrowdfundingOptions={(value) =>
                      updateFormData('monthlyCrowdfundingOptions', value)
                    }
                    annuallyCrowdfundingOptions={formData.annuallyCrowdfundingOptions}
                    setAnnuallyCrowdfundingOptions={(value) =>
                      updateFormData('annuallyCrowdfundingOptions', value)
                    }
                    // Product Groups props (Legacy)
                    productGroups={formData.productGroups}
                    setProductGroups={(value) => updateFormData('productGroups', value)}
                    // Per-frequency Product Groups props (NEW)
                    oneTimeProductGroups={formData.oneTimeProductGroups}
                    setOneTimeProductGroups={(value) =>
                      updateFormData('oneTimeProductGroups', value)
                    }
                    dailyProductGroups={formData.dailyProductGroups}
                    setDailyProductGroups={(value) => updateFormData('dailyProductGroups', value)}
                    weeklyProductGroups={formData.weeklyProductGroups}
                    setWeeklyProductGroups={(value) => updateFormData('weeklyProductGroups', value)}
                    monthlyProductGroups={formData.monthlyProductGroups}
                    setMonthlyProductGroups={(value) =>
                      updateFormData('monthlyProductGroups', value)
                    }
                    annuallyProductGroups={formData.annuallyProductGroups}
                    setAnnuallyProductGroups={(value) =>
                      updateFormData('annuallyProductGroups', value)
                    }
                    // Checkout props
                    selectedCountry={formData.selectedCountry}
                    setSelectedCountry={(value) => updateFormData('selectedCountry', value)}
                    // Rich text editor props
                    editorState={editorState}
                    setEditorState={setEditorState}
                    // Fundraiser type props
                    enableEmbedForm={formData.enableEmbedForm}
                    setEnableEmbedForm={(value) => updateFormData('enableEmbedForm', value)}
                    showAnonymousDonations={formData.showAnonymousDonations}
                    setShowAnonymousDonations={(value) =>
                      updateFormData('showAnonymousDonations', value)
                    }
                    showOrganizationNameField={formData.showOrganizationNameField}
                    setShowOrganizationNameField={(value) =>
                      updateFormData('showOrganizationNameField', value)
                    }
                    enablePhoneNumber={formData.enablePhoneNumber}
                    setEnablePhoneNumber={(value) => updateFormData('enablePhoneNumber', value)}
                    enableBillingAddress={formData.enableBillingAddress}
                    setEnableBillingAddress={(value) =>
                      updateFormData('enableBillingAddress', value)
                    }
                    enableDoubleTheDonation={formData.enableDoubleTheDonation}
                    setEnableDoubleTheDonation={(value) =>
                      updateFormData('enableDoubleTheDonation', value)
                    }
                    enableCommunicationConsent={formData.enableCommunicationConsent}
                    setEnableCommunicationConsent={(value) =>
                      updateFormData('enableCommunicationConsent', value)
                    }
                    communicationConsentMessage={formData.communicationConsentMessage}
                    setCommunicationConsentMessage={(value) =>
                      updateFormData('communicationConsentMessage', value)
                    }
                    enableDedication={formData.enableDedication}
                    setEnableDedication={(value) => updateFormData('enableDedication', value)}
                    dedicationTypes={formData.dedicationTypes}
                    setDedicationTypes={(value) => updateFormData('dedicationTypes', value)}
                    enableDedicationMessage={formData.enableDedicationMessage}
                    setEnableDedicationMessage={(value) =>
                      updateFormData('enableDedicationMessage', value)
                    }
                    lockCoverFees={formData.lockCoverFees}
                    setLockCoverFees={(value) => updateFormData('lockCoverFees', value)}
                    disableCheckoutBump={formData.disableCheckoutBump}
                    setDisableCheckoutBump={(value) => updateFormData('disableCheckoutBump', value)}
                    formType={formData.formType}
                    setFormType={(value) => updateFormData('formType', value)}
                    fundraiserType={formData.fundraiserType}
                    setFundraiserType={(value) => updateFormData('fundraiserType', value)}
                    showFieldMappingModal={showFieldMappingModal}
                    setShowFieldMappingModal={setShowFieldMappingModal}
                    integrationType={integrationType}
                    fieldMappings={formData.fieldMappings}
                    setFieldMappings={(value) => updateFormData('fieldMappings', value)}
                    // Salesforce Campaign ID props
                    salesforceCampaignIDICNAValues={salesforceCampaignIDICNAValues}
                    setSalesforceCampaignIDICNAValues={setSalesforceCampaignIDICNAValues}
                    setSalesforceFieldMapperOpen={setSalesforceFieldMapperOpen}
                    // Feature flags
                    dtdPublicApiKey={formData.dtdPublicApiKey}
                  />
                )}

                {/* Other tabs would go here */}
                {activeTab.name === 'Custom Input Fields' && (
                  <FormWorkspaceTabSection
                    tabName="Custom Input Fields"
                    enabled={props.presentation === 'workspace'}
                  >
                    <CustomInputsTab
                      showSectionHeader={props.presentation !== 'workspace'}
                      customInputs={formData.customInputs}
                      setCustomInputs={(value) => updateFormData('customInputs', value)}
                      isFieldInvalid={isFieldInvalid}
                      handleFieldFocus={handleFieldFocus}
                      setInvalidFields={setInvalidFields}
                    />
                  </FormWorkspaceTabSection>
                )}

                {activeTab.name === 'FAQs' && (
                  <FormWorkspaceTabSection
                    tabName="FAQs"
                    enabled={props.presentation === 'workspace'}
                  >
                    <CustomFAQsTab
                      showSectionHeader={props.presentation !== 'workspace'}
                      customFAQs={formData.faqs}
                      setCustomFAQs={(value) => updateFormData('faqs', value)}
                      handleFieldFocus={handleFieldFocus}
                      isFieldInvalid={isFieldInvalid}
                      setInvalidFields={setInvalidFields}
                    />
                  </FormWorkspaceTabSection>
                )}

                {activeTab.name === 'Emails' && (
                  <FormWorkspaceTabSection
                    tabName="Emails"
                    enabled={props.presentation === 'workspace'}
                  >
                    <EmailConfigurationTab
                      showSectionHeader={props.presentation !== 'workspace'}
                      emailConfig={formData.emailConfig}
                      setEmailConfig={(value) => updateFormData('emailConfig', value)}
                      isFieldInvalid={isFieldInvalid}
                      handleFieldFocus={handleFieldFocus}
                      setInvalidFields={setInvalidFields}
                    />
                  </FormWorkspaceTabSection>
                )}
              </div>
            </div>
          </ScrollFade>
        </div>
      </SheetContent>

      {/* QR Code Modal */}
      <QRCodeModal
        open={showQRModal && paymentsReady && formData.active === true}
        setOpen={setShowQRModal}
        url={savedQRCodeData?.url || `${HOSTED_PAGE_URL}/donate/${formData.elementID}`}
        title="Fundraiser QR Code"
        label={'Scan to Donate'}
        instructionText="Download the QR Code below to redirect people to this Form."
        bannerColor={formData.color}
      />
      <FieldMapperModal
        open={showFieldMappingModal}
        funds={formData.funds}
        bloomerangAppeals={bloomerangAppeals}
        bloomerangCampaigns={bloomerangCampaigns}
        bloomerangFunds={bloomerangFunds}
        virtuousProjects={virtuousProjects}
        productGroups={formData.productGroups}
        customInputs={formData.customInputs}
        integrationType={integrationType}
        // open={true}
        setOpen={setShowFieldMappingModal}
        objectType="Field Mapping"
        originalMappings={formData.fieldMappings}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setOriginalMappings={(value: any) => updateFormData('fieldMappings', value)}
      />

      {/* Salesforce Field Mapper Modal */}
      <SalesforceFieldMapperModal
        open={salesforceFieldMapperOpen}
        setOpen={setSalesforceFieldMapperOpen}
        formName={formData.formName || 'Fundraiser'}
        campaignId={salesforceCampaignIDICNAValues}
        setCampaignId={setSalesforceCampaignIDICNAValues}
      />
    </Sheet>
  );
}
